import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
  useReducer,
  useMemo,
} from 'react'

import { Howl, Howler } from 'howler'

export const Actions = {
  ON_LOAD: 'ON_LOAD',
  ON_PLAY: 'ON_PLAY',
  ON_END: 'ON_END',
  ON_PAUSE: 'ON_PAUSE',
  ON_STOP: 'ON_STOP',
  ON_SEEK: 'ON_SEEK',
  ON_WAVE_SEEK: 'ON_WAVE_SEEK',
  ON_PLAY_ERROR: 'ON_PLAY_ERROR',
  ON_LOAD_ERROR: 'ON_LOAD_ERROR',
  RESET_ERROR: 'RESET_ERROR',
}

export const initialState = {
  loading: true,
  playing: false,
  stopped: true,
  error: null,
  seek: {
    src: '',
    value: 0,
    percentage: 0,
  },
  history: {},
}

export function reducer(state, action) {
  switch (action.type) {
    case Actions.ON_LOAD:
      return {
        ...state,
        stopped: true,
        loading: false,
      }
    case Actions.ON_PLAY:
      return {
        ...state,
        playing: true,
        stopped: false,
      }
    case Actions.ON_STOP:
    case Actions.ON_END:
      state.history[action.payload.src] = {
        value: action.payload.seekValue,
        percentage: action.payload.seekPercentage,
      }
      return {
        ...state,
        stopped: true,
        playing: false,
        history: state.history,
      }
    case Actions.ON_PAUSE: {
      state.history[action.payload.src] = {
        value: action.payload.seekValue,
        percentage: action.payload.seekPercentage,
      }
      return {
        ...state,
        playing: false,
        history: state.history,
      }
    }
    case Actions.ON_WAVE_SEEK: {
      state.history[action.payload.src] = {
        value: action.payload.seekValue,
        percentage: action.payload.seekPercentage,
      }
      return {
        ...state,
        history: state.history,
      }
    }
    case Actions.ON_PLAY_ERROR:
      return {
        ...state,
        playing: false,
        stopped: true,
        error: action.error,
      }
    case Actions.ON_LOAD_ERROR:
      return {
        ...state,
        playing: false,
        stopped: true,
        loading: false,
        error: action.error,
      }
    case Actions.RESET_ERRORS:
      return {
        ...state,
        error: null,
      }
    case Actions.ON_SEEK:
      return {
        ...state,
        seek: {
          src: action.payload.src,
          value: action.payload.seekPercentage,
        },
      }
    default:
      return state
  }
}

const noop = () => {
  console.error('No Operation')
}

export const MediaPlayerContext = React.createContext({})

const initialMeta = {
  id: '',
  sku: '',
  displayName: '',
  product: '',
  voiceage: '',
  langauge: '',
}

export function MediaPlayerProvider({ children }) {
  const [player, setPlayer] = useState(null)
  const [{ loading, error, playing, stopped, seek, history }, dispatch] =
    useReducer(reducer, initialState)

  const [meta, setMeta] = useState()

  const playerRef = useRef()

  let lastSrcToLoad = ''

  const constructHowl = useCallback(({ src, format }) => {
    lastSrcToLoad = src
    const howl = new Howl({
      src,
      format,
      onload: () => {
        if (lastSrcToLoad === src) {
          if (history && history.hasOwnProperty(src)) {
            howl.seek(history[src].value)
          }
          howl.play()
        }
      },
    })

    return howl
  }, [])

  function loadMeta(nextMeta) {
    if (nextMeta) {
      const { id, sku, displayName, product, voiceage, language } = nextMeta
      setMeta({ id, sku, displayName, product, voiceage, language })
    } else {
      setMeta({})
    }
  }

  function onWaveSeek({ srcUrl, time, percentage }) {
    dispatch({
      type: Actions.ON_WAVE_SEEK,
      payload: {
        src: srcUrl,
        seekValue: time,
        seekPercentage: percentage,
      },
    })
    if (playerRef.current && srcUrl == playerRef.current._src) {
      playerRef.current.seek(time)
    }
  }

  const load = useCallback(
    ({ src, format }) => {
      dispatch({ type: Actions.RESET_ERRORS })

      let wasPlaying = false
      if (playerRef.current) {
        // Don't do anything if we're asked to reload the same source
        if (playerRef.current._src === src) return
        wasPlaying = playerRef.current.playing()
        if (wasPlaying) {
          playerRef.current.stop()
          // Remove event handlers from player that is about to be replaced
          playerRef.current.off()
          playerRef.current = undefined
        }
      }

      const howl = constructHowl({
        src,
        format,
      })

      // If this howl has already been loaded then there is no need to change loading state
      if (howl._state !== 'loaded') {
        dispatch({ type: Actions.ON_LOAD })
      }

      let seekInterval = null

      howl.on('load', () => void dispatch({ type: Actions.ON_LOAD }))
      howl.on('play', function (self) {
        if (!howl.playing()) return // prevents howl from playing the same song twice
        dispatch({ type: Actions.ON_PLAY })

        seekInterval = setInterval(() => {
          dispatch({
            type: Actions.ON_SEEK,
            payload: {
              src: howl._src,
              seekValue: howl.seek(),
              seekPercentage: howl.seek() / howl.duration(),
            },
          })
        }, 200)
      })
      howl.on('end', () => {
        dispatch({
          type: Actions.ON_END,
          payload: {
            src: howl._src,
            seekValue: howl.seek(),
            seekPercentage: 1,
          },
        })
        if (seekInterval) clearInterval(seekInterval)
      })
      howl.on('pause', () => {
        dispatch({
          type: Actions.ON_PAUSE,
          payload: {
            src: howl._src,
            seekValue: howl.seek(),
            seekPercentage: howl.seek() / howl.duration(),
          },
        })
        if (seekInterval) clearInterval(seekInterval)
      })
      howl.on('stop', () => {
        dispatch({
          type: Actions.ON_STOP,
          payload: {
            src: howl._src,
            seekValue: 0,
            seekPercentage: 0,
          },
        })
        if (seekInterval) clearInterval(seekInterval)
      })
      howl.on('playerror', (_id, error) => {
        dispatch({
          type: Actions.ON_PLAY_ERROR,
          error: new Error('[Play error] ' + error),
        })
        if (seekInterval) clearInterval(seekInterval)
      })
      howl.on('loaderror', (_id, error) => {
        dispatch({
          type: Actions.ON_LOAD_ERROR,
          error: new Error('[Load error] ' + error),
        })
      })

      setPlayer(howl)
      playerRef.current = howl
    },
    [constructHowl]
  )

  useEffect(() => {
    return () => {
      if (playerRef.current) playerRef.current.unload()
    }
  }, [])

  return (
    <MediaPlayerContext.Provider
      value={{
        player,
        load,
        error,
        loading,
        playing,
        stopped,
        meta,
        loadMeta,
        seek,
        history,
        onWaveSeek,
        ready: !loading && !error,
      }}
    >
      {children}
    </MediaPlayerContext.Provider>
  )
}

export const useMediaPlayer = (props) => {
  const { player, load, ...context } = useContext(MediaPlayerContext)

  const { src, format } = props || {}

  useEffect(() => {
    // if useMediaPlayer is called without arguments
    // don't do anything: the user will have access
    // to the current context
    if (!src) return
    load({ src, format })
  }, [src, format, load])

  return {
    ...context,
    play: () => (player ? player.play() : noop()),
    pause: () => (player ? player.pause() : noop()),
    pause: () => (player ? player.pause() : noop()),
    stop: () => (player ? player.stop() : noop()),
    mute: () => (player ? player.mute() : noop()),
    seek: (to) => (player ? player.seek(to) : noop()),
    load,
  }
}

export const usePosition = (config = {}) => {
  const { highRefreshRate = false } = config
  const { player, playing, stopped } = useContext(MediaPlayerContext)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    if (player) {
      if (player.seek() instanceof Howl) {
        setPosition(0)
      } else {
        setPosition(player.seek())
      }
      setDuration(player.duration())
    }
  }, [player, stopped])

  useEffect(() => {
    let timeout
    if (!highRefreshRate && player && playing) {
      timeout = window.setInterval(() => {
        if (player.seek() instanceof Howl) {
          setPosition(0)
        } else {
          setPosition(player.seek())
        }
      }, 1000)
    }
    return () => clearTimeout(timeout)
  }, [highRefreshRate, player, playing])

  useEffect(() => {
    let frame
    const animate = () => {
      if (player.seek() instanceof Howl) {
        setPosition(0)
      } else {
        setPosition(player.seek())
      }
      frame = requestAnimationFrame(animate)
    }

    if (highRefreshRate && player && playing) {
      animate()
    }

    return () => {
      if (frame) {
        cancelAnimationFrame(frame)
      }
    }
  }, [highRefreshRate, player, playing])

  return { position, duration }
}

export const withMediaPlayer = (Component) => (props) => {
  const { load } = useMediaPlayer()

  return <Component {...props} load={load} />
}
